import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { finalize, forkJoin, Observable } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

// Components
import { NavbarComponent } from "../../../template/components/navbar/navbar.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { EntityInfoComponent } from "../../../shared/components/entity-info/entity-info.component";

// Models
import { GroupListElem } from '../../models/group.model';
import { MultiAction } from '../../../shared/models/multi-action.model';
import { Column } from '../../../shared/models/column.model';

// Services
import { GroupService } from '../../services/group.service';
import { ToastService } from '../../../shared/services/toast.service';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { AuthService } from '../../../auth/services/auth.service';


@Component({
    selector: 'app-group-list',
    imports: [NavbarComponent, TableComponent, AsyncPipe, NgxPermissionsModule, EntityInfoComponent],
    templateUrl: './group-list.component.html'
})
export class GroupListComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly groupSrv = inject(GroupService);
  private readonly toastSrv = inject(ToastService);
  private readonly permissionsSrv = inject(NgxPermissionsService);
  private readonly authSrv = inject(AuthService);
  private readonly modalSrv = inject(NgbModal);

  searchValue: string = '';
  groups$!: Observable<GroupListElem[]>;
  loading: boolean = false;

  navList: { label: string, routePath: string }[] = [];
  multiActions!: MultiAction[];

  groupColumns: Column[] = [
    { label: 'Id', key: 'id', type: 'hidden' },
    { label: $localize`Nome`, key: 'name', type: 'string' },
    { label: $localize`Descrizione`, key: 'description', type: 'string' },
    { label: $localize`Utenti`, key: 'userCount', type: 'user-counter' },
    { label: $localize`Contesti`, key: 'contextCount', type: 'counter' }
  ];

  hasMadPermission: boolean = false;

  ngOnInit(): void {
    this.permissionsSrv.hasPermission('ROLE_MAD').then((hasPermission) => {
      this.hasMadPermission = hasPermission;
      this.setNavigationAndActions();
      this.fetchGroups();
    });
  }

  private setNavigationAndActions(): void {
    if (this.hasMadPermission) {
      this.navList = [{ label: 'Lista Gruppi', routePath: '/groups' }];
      this.multiActions = [{ label: 'Elimina', action: 'delete', buttonType: 'danger' }];
    } else {
      this.navList = [{ label: 'I tuoi Gruppi', routePath: '/groups' }];
      this.groupColumns.push({ label: '', key: '', type: 'action', actionType: 'unlink' });
    }
  }

  private fetchGroups(): void {
    this.loading = true;

    this.groups$ = (this.hasMadPermission
      ? this.groupSrv.listGroups()
      : this.groupSrv.listMyGroups(this.authSrv.getUserId())).pipe(
        finalize(() => {
          this.loading = false; // Set loading to false when the observable completes or errors out
        })
      );
  }

  getFilteredGroups(groups: GroupListElem[]): GroupListElem[] {
    if (!this.searchValue) return groups;
    const lowerCaseSearchValue = this.searchValue.toLowerCase();
    return groups.filter(user =>
      user.name.toLowerCase().includes(lowerCaseSearchValue) ||
      user.description.toLowerCase().includes(lowerCaseSearchValue)
    );
  }

  openGroupDetail(group: GroupListElem): void {
    if (this.hasMadPermission) {
      this.router.navigate(['/groups', group.id]);
    }
  }

  deleteGroups(groups: GroupListElem[]): void {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal' });
    modalRef.componentInstance.title = 'Elimina Gruppi';
    modalRef.componentInstance.body = 'Stai eliminando i Gruppi:';
    modalRef.componentInstance.list = groups.map(el => el.name);
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
        this.apiDeleteGroups(groups);
      },
      (reason) => {
        console.log(`Dismissed`);
      },
    );
  }

  private apiDeleteGroups(groups: GroupListElem[]): void {
    const deleteRequests = groups.map(group => this.groupSrv.deleteGroup(group.id));

    if (this.hasMadPermission) {
      forkJoin(deleteRequests).subscribe({
        next: () => {
          this.toastSrv.show({ type: 'success', message: $localize`Gruppi eliminati con successo` });
          this.fetchGroups();  // Refresh the list after deletion
        },
        error: (error) => {
          console.error('Error during multiple groups deletion:', error);
          this.toastSrv.show({ type: 'error', message: $localize`Errore durante l\'eliminazione di alcuni gruppi` });
        }
      });
    }
  }

  unlinkGroup(group: GroupListElem): void {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.title = $localize`Abbandona Gruppo`;
    modalRef.componentInstance.body = $localize`Stai abbandonando il gruppo ${group.name}`; // TODO: trovare modo alternativo per recuperare il nome gruppo
    modalRef.componentInstance.enableMotivationForm = true;
    modalRef.result.then(
      (result: { motivation: string }) => {
        console.debug(`Closed with: ${result}`);
        this.groupSrv.leaveGroup(this.authSrv.getUserId(), group.id, result.motivation).subscribe({
          next: () => {
            this.toastSrv.show({ type: 'success', message: $localize`Hai abbandonato il gruppo` });
            this.fetchGroups();  // Refresh the list after deletion
          },
          error: (error) => {
            console.error('Error during multiple groups deletion:', error);
            this.toastSrv.show({ type: 'error', message: $localize`Errore durante la disassociazione dal gruppo` });
          }
        });
      },
      (reason) => {
        console.debug(`Dismissed`);
      },
    );
  }
}
